import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService } from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching menu types with caching
 */
export const useMenuTypes = (schoolId, options = {}) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  console.log(`[MenuTypeDebug] useMenuTypes hook initialized with schoolId: ${schoolId}, language: ${language}`);

  return useQuery({
    queryKey: ['menuTypes', schoolId, language],
    queryFn: async () => {
      console.log(`[MenuTypeDebug] Fetching menu types for schoolId: ${schoolId}, language: ${language}`);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const token = localStorage.getItem('accessToken');
        
        // Add timestamp to prevent browser caching
        const timestamp = Date.now();
        const url = `${API_BASE_URL}/api/admin/menu-types?schoolId=${schoolId}&language=${language}&_=${timestamp}`;
        
        console.log(`[MenuTypeDebug] Direct API call to: ${url}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // Prevent browser from using cached responses
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const result = await response.json();
        console.log(`[MenuTypeDebug] Menu types fetch successful, received ${result?.length || 0} items:`, result);
        return result || [];
      } catch (error) {
        console.error('[MenuTypeDebug] Error fetching menu types:', error);
        return []; // Return empty array instead of throwing to prevent UI issues
      }
    },
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error('[MenuTypeDebug] Error in useMenuTypes query:', error);
    },
    // Allow overriding the default options with custom options
    ...options
  });
};

/**
 * Custom hook for fetching a single menu type's details
 */
export const useMenuType = (menuTypeId) => {
  return useQuery({
    queryKey: ['menuType', menuTypeId],
    queryFn: () => menuService.getMenuTypeTranslations(menuTypeId),
    enabled: !!menuTypeId,
  });
};

/**
 * Custom hook for creating a menu type with optimistic updates
 */
export const useCreateMenuType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ schoolId, name, translations = {}, isVisible = true }) => 
      menuService.createMenuType(schoolId, name, translations, isVisible),
    
    onMutate: async ({ schoolId, name, translations = {}, isVisible = true }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypes', schoolId, language],
      });

      // Snapshot the previous value
      const previousMenuTypes = queryClient.getQueryData(['menuTypes', schoolId, language]);

      // Optimistically update with new menu type
      if (previousMenuTypes) {
        const optimisticMenuType = {
          id: `temp-${Date.now()}`,
          school_id: schoolId,
          name: name,
          is_visible: isVisible,
          created_at: new Date().toISOString(),
        };

        queryClient.setQueryData(
          ['menuTypes', schoolId, language],
          [...previousMenuTypes, optimisticMenuType]
        );
      }

      return { previousMenuTypes };
    },
    
    onError: (err, { schoolId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId, language],
          context.previousMenuTypes
        );
      }
    },
    
    onSettled: (data, error, { schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuTypes', schoolId, language],
      });
      
      // Also invalidate public menu data to update it on the public side
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
    },
  });
};

/**
 * Custom hook for updating a menu type with optimistic updates
 */
export const useUpdateMenuType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ id, translations = {}, isVisible }) => 
      menuService.updateMenuType(id, translations, isVisible),
    
    onMutate: async ({ id, schoolId, translations = {}, isVisible }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypes', schoolId, language],
      });
      await queryClient.cancelQueries({
        queryKey: ['menuType', id],
      });

      // Snapshot previous values
      const previousMenuTypes = queryClient.getQueryData(['menuTypes', schoolId, language]);
      const previousMenuType = queryClient.getQueryData(['menuType', id]);

      // Optimistically update menu types list
      if (previousMenuTypes) {
        const updatedMenuTypes = previousMenuTypes.map(menuType =>
          menuType.id === id
            ? { 
                ...menuType, 
                name: translations[language] || menuType.name,
                is_visible: isVisible !== undefined ? isVisible : menuType.is_visible
              }
            : menuType
        );

        queryClient.setQueryData(['menuTypes', schoolId, language], updatedMenuTypes);
      }

      // Optimistically update single menu type
      if (previousMenuType && translations) {
        queryClient.setQueryData(
          ['menuType', id], 
          { ...previousMenuType, ...translations }
        );
      }

      return { previousMenuTypes, previousMenuType };
    },
    
    onError: (err, { id, schoolId }, context) => {
      // If the mutation fails, revert to the previous values
      if (context?.previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId, language],
          context.previousMenuTypes
        );
      }
      
      if (context?.previousMenuType) {
        queryClient.setQueryData(['menuType', id], context.previousMenuType);
      }
    },
    
    onSettled: (data, error, { id, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuTypes', schoolId, language],
      });
      queryClient.invalidateQueries({
        queryKey: ['menuType', id],
      });
      
      // Also invalidate related course data
      queryClient.invalidateQueries({
        queryKey: ['menuTypeCourses', id],
      });
      queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey[0] === 'availableCoursesForMenuType' && 
          query.queryKey[1] === id
      });
      
      // Also invalidate public menu data to update it on the public side
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
    },
  });
};

/**
 * Custom hook for deleting a menu type with optimistic updates
 */
export const useDeleteMenuType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: (id) => menuService.deleteMenuType(id),
    
    onMutate: async ({ id, schoolId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuTypes', schoolId, language],
      });

      // Snapshot the previous value
      const previousMenuTypes = queryClient.getQueryData(['menuTypes', schoolId, language]);

      // Optimistically update the cache by removing the menu type
      if (previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId, language],
          previousMenuTypes.filter(menuType => menuType.id !== id)
        );
      }

      return { previousMenuTypes };
    },
    
    onError: (err, { id, schoolId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousMenuTypes) {
        queryClient.setQueryData(
          ['menuTypes', schoolId, language],
          context.previousMenuTypes
        );
      }
    },
    
    onSettled: (data, error, { id, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuTypes', schoolId, language],
      });
      
      // Also remove from cache
      queryClient.removeQueries({
        queryKey: ['menuType', id],
      });
      queryClient.removeQueries({
        queryKey: ['menuTypeCourses', id],
      });
      
      // Also invalidate availability since it may reference this menu type
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
      
      // Also invalidate public menu data to update it on the public side
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
};

/**
 * Custom hook for updating a menu type's translation
 */
export const useUpdateMenuTypeTranslation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ menuTypeId, languageCode, translation }) => 
      menuService.updateMenuTypeTranslation(menuTypeId, languageCode, translation),
    
    onMutate: async ({ menuTypeId, languageCode, translation, schoolId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuType', menuTypeId],
      });

      // Snapshot the previous value
      const previousTranslations = queryClient.getQueryData(['menuType', menuTypeId]);

      // Optimistically update the translation
      if (previousTranslations) {
        const updatedTranslations = { ...previousTranslations };
        updatedTranslations[languageCode] = translation;
        
        queryClient.setQueryData(['menuType', menuTypeId], updatedTranslations);
      }

      return { previousTranslations };
    },
    
    onError: (err, { menuTypeId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousTranslations) {
        queryClient.setQueryData(
          ['menuType', menuTypeId],
          context.previousTranslations
        );
      }
    },
    
    onSettled: (data, error, { menuTypeId, schoolId, languageCode }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuType', menuTypeId],
      });
      
      // Also invalidate the menu types list if school ID is provided
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ['menuTypes', schoolId, languageCode],
        });
      }
      
      // Also invalidate public menu data to update it on the public side
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
    },
  });
}; 